/**
 * Cross-Tab Synchronization Service
 *
 * Ensures authentication state is synchronized across all browser tabs/windows.
 * Critical for security - prevents logged-out tabs from appearing authenticated.
 *
 * Methods:
 * 1. BroadcastChannel API (primary) - Modern, direct tab communication
 * 2. localStorage events (fallback) - Works in all browsers
 * 3. Periodic validation - Checks session validity every 30 seconds
 */

type SyncEventType = 'LOGOUT' | 'LOGIN' | 'SESSION_EXPIRED';

interface SyncMessage {
  type: SyncEventType;
  timestamp: number;
  tabId: string;
}

type SyncCallback = (event: SyncEventType) => void;

class CrossTabSyncService {
  private broadcastChannel: BroadcastChannel | null = null;
  private tabId: string;
  private callbacks: Set<SyncCallback> = new Set();
  private sessionCheckInterval: number | null = null;
  private visibilityChangeHandler: (() => void) | null = null;
  private readonly STORAGE_KEY = 'auth_sync_event';
  private readonly SESSION_CHECK_INTERVAL = 30000; // 30 seconds

  constructor() {
    this.tabId = this.generateTabId();
    this.initializeBroadcastChannel();
    this.initializeStorageListener();
    this.startPeriodicSessionCheck();
    this.initializeVisibilityHandler();
  }

  private generateTabId(): string {
    return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Initialize BroadcastChannel for modern browsers
   * Primary method - direct communication between tabs
   */
  private initializeBroadcastChannel() {
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        this.broadcastChannel = new BroadcastChannel('auth_sync');

        this.broadcastChannel.addEventListener('message', (event) => {
          const message = event.data as SyncMessage;

          // Ignore messages from our own tab
          if (message.tabId === this.tabId) {
            return;
          }

          console.log(`[CrossTabSync] Received ${message.type} from another tab`);
          this.notifyCallbacks(message.type);
        });

        console.log('[CrossTabSync] BroadcastChannel initialized');
      }
    } catch (error) {
      console.warn('[CrossTabSync] BroadcastChannel not available:', error);
    }
  }

  /**
   * Initialize localStorage event listener
   * Fallback method for older browsers or when BroadcastChannel fails
   */
  private initializeStorageListener() {
    window.addEventListener('storage', (event) => {
      if (event.key === this.STORAGE_KEY && event.newValue) {
        try {
          const message = JSON.parse(event.newValue) as SyncMessage;

          // Ignore messages from our own tab
          if (message.tabId === this.tabId) {
            return;
          }

          console.log(`[CrossTabSync] Received ${message.type} via storage event`);
          this.notifyCallbacks(message.type);

          // Clean up the storage key after processing
          localStorage.removeItem(this.STORAGE_KEY);
        } catch (error) {
          console.error('[CrossTabSync] Failed to parse storage event:', error);
        }
      }
    });

    console.log('[CrossTabSync] Storage listener initialized');
  }

  /**
   * Start periodic session validation
   * Checks every 30 seconds if the session is still valid
   */
  private startPeriodicSessionCheck() {
    // Check immediately on start
    this.validateSession();

    // Then check every 30 seconds
    this.sessionCheckInterval = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        this.validateSession();
      }
    }, this.SESSION_CHECK_INTERVAL);

    console.log('[CrossTabSync] Periodic session validation started');
  }

  /**
   * Initialize visibility change handler
   * Validates session when user switches back to the tab
   */
  private initializeVisibilityHandler() {
    this.visibilityChangeHandler = () => {
      if (document.visibilityState === 'visible') {
        console.log('[CrossTabSync] Tab became visible, validating session...');
        this.validateSession();
      }
    };

    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    console.log('[CrossTabSync] Visibility change handler initialized');
  }

  /**
   * Validate session with backend
   * If session is invalid, trigger logout across all tabs
   */
  private async validateSession() {
    try {
      // Only validate if we have auth data (HIPAA: sessionStorage only)
      const hasToken = sessionStorage.getItem('authToken'); // Fixed: was 'token', should be 'authToken'

      if (!hasToken) {
        // No auth data, nothing to validate
        return;
      }

      // Make a lightweight check to backend
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'https://api.proviarcm.com/api'}/auth/check`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(hasToken && { 'Authorization': `Bearer ${hasToken}` })
          }
        }
      );

      if (response.status === 401) {
        // Session is invalid
        console.log('[CrossTabSync] Session validation failed - triggering logout');
        this.notifyCallbacks('SESSION_EXPIRED');
      }
    } catch (error) {
      // Network error or other issue - don't trigger logout
      console.warn('[CrossTabSync] Session validation error:', error);
    }
  }

  /**
   * Broadcast an event to all other tabs
   */
  broadcast(type: SyncEventType) {
    const message: SyncMessage = {
      type,
      timestamp: Date.now(),
      tabId: this.tabId
    };

    // Method 1: BroadcastChannel (primary)
    if (this.broadcastChannel) {
      try {
        this.broadcastChannel.postMessage(message);
        console.log(`[CrossTabSync] Broadcasted ${type} via BroadcastChannel`);
      } catch (error) {
        console.error('[CrossTabSync] BroadcastChannel error:', error);
      }
    }

    // Method 2: localStorage event (fallback)
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(message));
      // Clean up after a short delay to trigger the event
      setTimeout(() => {
        localStorage.removeItem(this.STORAGE_KEY);
      }, 100);
      console.log(`[CrossTabSync] Broadcasted ${type} via localStorage`);
    } catch (error) {
      console.error('[CrossTabSync] localStorage error:', error);
    }
  }

  /**
   * Subscribe to sync events
   */
  subscribe(callback: SyncCallback): () => void {
    this.callbacks.add(callback);

    // Return unsubscribe function
    return () => {
      this.callbacks.delete(callback);
    };
  }

  /**
   * Notify all subscribers
   */
  private notifyCallbacks(event: SyncEventType) {
    this.callbacks.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error('[CrossTabSync] Callback error:', error);
      }
    });
  }

  /**
   * Clean up resources
   */
  destroy() {
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
    }

    if (this.sessionCheckInterval !== null) {
      clearInterval(this.sessionCheckInterval);
    }

    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }

    this.callbacks.clear();
    console.log('[CrossTabSync] Service destroyed');
  }
}

// Create singleton instance
export const crossTabSync = new CrossTabSyncService();
export default crossTabSync;
